/**
 * useVoiceRecorder — premium voice recording hook for chat inputs.
 *
 * Owns: MediaRecorder + Web Audio AnalyserNode (live audio levels) +
 * Whisper upload to /api/transcribe. The parent renders the UI:
 *  - mic button to call `start()`
 *  - while `isActive`, render <RecordingBar /> with `levels`/`seconds`/
 *    `confirm`/`cancel` (rocket should be hidden during recording).
 *
 * Auto-stops at `maxSeconds` (default 300 = 5 min) and surfaces a
 * `notice` string the parent can show as a toast/banner.
 *
 * Never auto-sends — onTranscribed only fills the input.
 *
 * Uses XMLHttpRequest because fetch() is monkey-patched in this env.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const BAR_COUNT = 28;

function pickMimeType() {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  if (typeof MediaRecorder === 'undefined') return '';
  for (const t of candidates) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* noop */ }
  }
  return '';
}

function uploadAudio(blob, language, prompt) {
  return new Promise((resolve, reject) => {
    const ext = blob.type.includes('mp4') ? 'm4a'
      : blob.type.includes('ogg') ? 'ogg'
      : 'webm';
    const fd = new FormData();
    fd.append('file', blob, `voice.${ext}`);
    if (language) fd.append('language', language);
    if (prompt) fd.append('prompt', prompt);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/transcribe`, true);
    xhr.timeout = 60000;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { /* noop */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data?.detail || `Transcription failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Network error during transcription'));
    xhr.ontimeout = () => reject(new Error('Transcription timed out'));
    xhr.send(fd);
  });
}

const ZERO_LEVELS = new Array(BAR_COUNT).fill(0);

export function useVoiceRecorder({
  language = 'es',
  prompt = '',
  maxSeconds = 300,
  onTranscribed,
  onError,
} = {}) {
  const [state, setState] = useState('idle'); // idle | requesting | recording | uploading | error
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState(ZERO_LEVELS);
  const [notice, setNotice] = useState('');

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const maxStopRef = useRef(null);
  const cancelledRef = useRef(false);

  const cleanupAudio = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (maxStopRef.current) { clearTimeout(maxStopRef.current); maxStopRef.current = null; }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    streamRef.current?.getTracks?.().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => () => {
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    cleanupAudio();
  }, [cleanupAudio]);

  const startMeter = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx || !streamRef.current) return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(streamRef.current);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.78;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      // Use lower-mid range bins (skip very-low rumble + very-high hiss).
      const startBin = 2;
      const usableBins = Math.max(BAR_COUNT, Math.min(buf.length - startBin, 56));
      const step = Math.max(1, Math.floor(usableBins / BAR_COUNT));

      const loop = () => {
        analyser.getByteFrequencyData(buf);
        const out = new Array(BAR_COUNT);
        for (let i = 0; i < BAR_COUNT; i++) {
          let s = 0;
          let count = 0;
          for (let j = 0; j < step; j++) {
            const idx = startBin + i * step + j;
            if (idx < buf.length) { s += buf[idx]; count++; }
          }
          // Slight gamma curve for visual punch.
          const v = count ? Math.pow((s / count) / 255, 0.7) : 0;
          out[i] = v;
        }
        setLevels(out);
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch (e) {
      // Meter is non-critical; recording still works without bars.
      console.warn('Voice meter unavailable:', e);
    }
  }, []);

  const start = useCallback(async () => {
    if (state === 'recording' || state === 'requesting' || state === 'uploading') return;
    if (!navigator.mediaDevices?.getUserMedia) {
      const err = new Error('Microphone not supported on this device');
      setNotice(err.message);
      onError?.(err);
      setState('error');
      setTimeout(() => { setState('idle'); setNotice(''); }, 2200);
      return;
    }
    setNotice('');
    setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      cancelledRef.current = false;

      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };

      rec.onstop = async () => {
        const cancelled = cancelledRef.current;
        const type = rec.mimeType || mimeType || 'audio/webm';
        const chunks = chunksRef.current;
        chunksRef.current = [];
        cleanupAudio();
        setLevels(ZERO_LEVELS);

        if (cancelled || chunks.length === 0) {
          setState('idle');
          setSeconds(0);
          return;
        }
        const blob = new Blob(chunks, { type });
        if (!blob.size) {
          setState('idle');
          setSeconds(0);
          return;
        }
        setState('uploading');
        try {
          const data = await uploadAudio(blob, language, prompt);
          const text = (data?.text || '').trim();
          if (text) onTranscribed?.(text);
          else setNotice('No speech detected. Try again.');
          setState('idle');
          setSeconds(0);
          if (!text) setTimeout(() => setNotice(''), 2400);
        } catch (err) {
          setNotice(err.message || 'Transcription failed');
          onError?.(err);
          setState('error');
          setTimeout(() => { setState('idle'); setNotice(''); }, 2600);
        }
      };

      recorderRef.current = rec;
      rec.start();
      setSeconds(0);

      tickRef.current = setInterval(() => {
        setSeconds(s => s + 1);
      }, 1000);

      // Hard auto-stop at maxSeconds (does NOT cancel; we transcribe what was captured).
      maxStopRef.current = setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          setNotice('Maximum recording time reached.');
          cancelledRef.current = false;
          try { recorderRef.current.stop(); } catch { /* noop */ }
          // Notice persists into uploading/idle; clear later.
          setTimeout(() => setNotice(''), 3000);
        }
      }, maxSeconds * 1000);

      startMeter();
      setState('recording');
    } catch (err) {
      cleanupAudio();
      const e = err instanceof Error ? err : new Error('Could not access microphone');
      setNotice(e.message);
      onError?.(e);
      setState('error');
      setTimeout(() => { setState('idle'); setNotice(''); }, 2400);
    }
  }, [state, language, prompt, onTranscribed, onError, maxSeconds, cleanupAudio, startMeter]);

  const confirm = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      cancelledRef.current = false;
      try { rec.stop(); } catch { /* noop */ }
    }
  }, []);

  const cancel = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      cancelledRef.current = true;
      try { rec.stop(); } catch { /* noop */ }
    } else {
      cleanupAudio();
      setLevels(ZERO_LEVELS);
      setState('idle');
      setSeconds(0);
      setNotice('');
    }
  }, [cleanupAudio]);

  return {
    state,
    isRecording: state === 'recording',
    isRequesting: state === 'requesting',
    isUploading: state === 'uploading',
    isError: state === 'error',
    isActive: state === 'recording' || state === 'requesting' || state === 'uploading',
    seconds,
    levels,
    notice,
    maxSeconds,
    start,
    confirm,
    cancel,
  };
}
